import { axiosInstance } from '@/utils/lib/axios';
import { create } from 'zustand';
import useTenantState from './tenant.state';
import { env } from '@/utils/constants/env';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { generateParams } from '@/utils/functions/generateParams';
import { toast } from 'react-toastify';
import { errorHandler } from '@/utils/functions/errorHandler';

export interface IIntegration {
  id: number;
  name: string;
  description: string;
  key: string;
  value: string;
  tenant_id: number;
  created_at: Date;
  updated_at: Date;
  decrypted_value: string;
}

interface IntegrationState {
  availableIntegration: { [key: string]: string };
  fetchAvailableIntegrations: () => void;
  integration: IIntegration[];
  allintegration: IIntegration[];
  totalElements: number;
  totalPages: number;
  isLoading: boolean;
  error: unknown;

  fetchIntergration: (integrationKey: string, paramsDataConfig: DataConfig) => Promise<void>;
  createIntegration: (integrationKey: string, formData: Record<string, any>) => Promise<any>;
  fetchAllIntegrations: () => Promise<any>;
  updateIntegration: (integrationKey: string, integrationId: number, formData: Record<string, any>) => Promise<any>;
  deleteIntegration: (integrationKey: string, integrationId: number) => void;
  testIntegrationConnection: (tenantId: number, integrationId: number, payload: Record<string, any>) => Promise<any>;
}

const useIntegrationState = create<IntegrationState>(set => ({
  availableIntegration: {},
  allintegration: [],
  fetchAvailableIntegrations: () => {
    const currentTenant = useTenantState.getState().currentTenant;
    if (!currentTenant) return;

    axiosInstance
      .get(`${env.apiTasklistBaseUrl}/api/v1/probis/integration/${currentTenant.id}/supported`)
      .then(res => set({ availableIntegration: res.data }))
      .catch(err => console.error('Failed to fetch integrations:', err));
  },

  integration: [],
  totalElements: 0,
  totalPages: 1,
  isLoading: false,
  error: null,

  fetchIntergration: (integrationKey, paramsDataConfig) => {
    set({ isLoading: true, error: null });
    const tenantId = useTenantState.getState().currentTenant?.id;
    const params = generateParams(paramsDataConfig);
    const url = `${env.apiTasklistBaseUrl}/api/v1/probis/integration/${tenantId}/${integrationKey}${params}`;

    return axiosInstance
      .get(url)
      .then(res => {
        const data = res.data;
        const integration = Array.isArray(data.data?.content) ? data.data.content : [];
        set({
          integration,
          totalElements: data.data.totalElements ?? integration.length,
          totalPages: data.data.totalPages ?? 1,
          isLoading: false,
        });
      })
      .catch(err => {
        console.error('Error fetching integrations:', err);
        set({ error: err, integration: [], isLoading: false });
      });
  },

  fetchAllIntegrations: async () => {
    const tenantId = useTenantState.getState().currentTenant?.id;
    const url = `${env.apiTasklistBaseUrl}/api/v1/probis/integration/${tenantId}/integrations`;

    try {
      const res = await axiosInstance.get(url);
      const data = res.data;
      const allintegration = Array.isArray(data.data) ? data.data : [];
      set({
        allintegration,
        isLoading: false,
      });
      return res;
    } catch (err) {
      console.error('Error fetching integrations:', err);
      set({ error: err, integration: [], isLoading: false });
      return err;
    }
  },

  createIntegration: async (integrationKey, formData) => {
    const tenantId = useTenantState.getState().currentTenant?.id;
    if (!tenantId) return;

    set({ isLoading: true });

    try {
      const resList = await axiosInstance.get(
        `${env.apiTasklistBaseUrl}/api/v1/probis/integration/${tenantId}/${integrationKey}?page=0&limit=100`,
      );

      const existingIntegrations = resList.data?.data?.content || [];
      let name = formData.name?.trim();
      if (!name) {
        const baseName = `${integrationKey} configuration`;
        name = baseName;
        let counter = 0;
        while (existingIntegrations.some((item: any) => item.name === name)) {
          counter++;
          name = `${baseName} (${counter})`;
        }
      }

      const res = await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/probis/integration/${tenantId}`, {
        key: integrationKey,
        name,
        description: formData.description || '',
        value: formData.value,
      });

      toast.success('Integration created successfully');

      const { fetchIntergration } = useIntegrationState.getState();
      fetchIntergration(integrationKey, { page: 0, limit: 10 });
      set({ isLoading: false });

      return res;
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
      throw err;
    }
  },

  updateIntegration: async (integrationKey, integrationId, formData) => {
    const tenantId = useTenantState.getState().currentTenant?.id;
    if (!tenantId) return;

    set({ isLoading: true });

    try {
      const res = await axiosInstance.put(
        `${env.apiTasklistBaseUrl}/api/v1/probis/integration/${tenantId}/${integrationId}`,
        {
          key: integrationKey,
          name: formData.name,
          description: formData.description,
          value: formData.value,
        },
      );

      toast.success('Integration updated successfully');

      const { fetchIntergration } = useIntegrationState.getState();
      fetchIntergration(integrationKey, { page: 0, limit: 10 });
      set({ isLoading: false });

      return res;
    } catch (err) {
      errorHandler(err);
      set({ isLoading: false, error: err });
      throw err;
    }
  },

  deleteIntegration: (integrationKey, integrationId) => {
    const tenantId = useTenantState.getState().currentTenant?.id;
    if (!tenantId) return;

    set({ isLoading: true });

    axiosInstance
      .delete(`${env.apiTasklistBaseUrl}/api/v1/probis/integration/${tenantId}/${integrationId}`)
      .then(res => {
        toast.success('Integration deleted successfully');
        const { fetchIntergration } = useIntegrationState.getState();
        fetchIntergration(integrationKey, { page: 0, limit: 10 });
        set({ isLoading: false });
      })
      .catch(err => {
        errorHandler(err);
        set({ isLoading: false, error: err });
      });
  },

  // BELOM PAKE ENDPOINT FIKSSS
  testIntegrationConnection: async (tenantId, integrationId, payload): Promise<{ message?: string }> => {
    set({ isLoading: true });
    try {
      const res = await axiosInstance.post(
        `https://da114963-f75f-495e-8cf3-e41a7dd50662.mock.pstmn.io/api/v1/probis/integration/${tenantId}/test/${integrationId}`,
        payload,
      );
      toast.success(res.data?.message || 'Connection successful!');
      set({ isLoading: false });
      return res.data;
    } catch (err) {
      errorHandler(err);
      toast.error('Connection test failed');
      set({ isLoading: false, error: err });
      throw err;
    }
  },
}));

export default useIntegrationState;
